import React, { useEffect, useRef, useState } from 'react'
import { ArrowDropDownIcon } from '@mui/x-date-pickers';
import AddIcon from '@mui/icons-material/Add';
import CustomSearch from '../../../components/CustomSearch';
import { useTranslation } from 'react-i18next';
import {
    ClickAwayListener,
    Grow,
    Link,
    MenuItem,
    Paper,
    Popper,
    Tooltip,
} from "@mui/material";
import { CustomAdvancedMenuList } from '../../../components/StyledComponents';
import ImportIcon20pxSvg from '../../../assets/images/icons/ImportIcon20pxSvg';
import ExportIcon16pxSvg from '../../../assets/images/icons/ExportIcon16pxSvg';
import { exportEventsVisitorsCSV } from '../../../axios/Visitor/VisitorApi';
import VisitorTabGridInfoTable from './VisitorTabGridInfoTable';
import AddParticipantDialog from './AddParticipantDialog';
import { startOfDay } from "date-fns";
import CalendarToday from "../../../assets/images/icons/CalendarToday";
import { useAuth } from "../../../context/AuthContext";
import { LoadingProvider, useLoading } from '../../../context/LoadingContext';
import dayjs from "dayjs";
import CustomDateTimePicker from '../../../components/CustomDateTimeRangePicker';
import { toast } from 'react-toastify';
import ImportIcon16pxSvg from '../../../assets/images/icons/ImportIcon16pxSvg';

const VisitorsTab = ({ refreshData, onRefresh, refresh, dataInfo }) => {
    const [openAdvanced, setOpenAdvanced] = useState(false);
    const { t } = useTranslation();
    const [selectedRow, setSelectedRow] = useState({});
    const [addParticipantsOpen, setAddParticipantsOpen] = useState(false);
    const { genaralSettingDateFormat } = useAuth();
    const [show, hide] = useLoading();

    const [selectedDate, setSelectedDate] = useState(
        startOfDay(new Date())
    );

    const [isDateFiltered, setIsDateFiltered] = useState(false);

    const [participantTotalPages, setParticipantTotalPages] = useState(1);
    const [participantPageSize, setParticipantPageSize] = useState(50);

    const anchorRefAdvanced = useRef(null);
    const [searchText, setSearchText] = useState("");

    const handleGoToToday = () => {
        setSelectedDate(startOfDay(new Date()));
        setIsDateFiltered(true);
    };

    const handleQuickFilterChange = (value) => {
        setSearchText(value);
    };

    const handleToggleAdvanced = () => {
        setOpenAdvanced((prev) => !prev);
    };

    const handleCloseAdvanced = (event) => {
        if (
            anchorRefAdvanced.current &&
            anchorRefAdvanced.current.contains(event.target)
        ) {
            return;
        }
        setOpenAdvanced(false);
    };

    const handlePaginationChange = ({ totalPages, pageSize }) => {
        setParticipantTotalPages(totalPages);
        setParticipantPageSize(pageSize);
    };

    function getCurrentTimestamp() {
        const now = new Date();
        const yyyy = now.getFullYear();
        const MM = String(now.getMonth() + 1).padStart(2, '0');
        const dd = String(now.getDate()).padStart(2, '0');
        const HH = String(now.getHours()).padStart(2, '0');
        const mm = String(now.getMinutes()).padStart(2, '0');
        const ss = String(now.getSeconds()).padStart(2, '0');
        return `${yyyy}${MM}${dd}${HH}${mm}${ss}`;
    }

    const exportParticipants = async () => {
        setOpenAdvanced(false);

        const payload = {
            eventId: dataInfo?.id || null,
            pageNo: participantTotalPages,
            recordPerPage: participantPageSize,
            status: "APPROVED",
            searchText: searchText || null,
            joinDate: isDateFiltered ? dayjs(selectedDate).format("YYYY-MM-DD") : null,
            langId: localStorage.getItem("i18nextLngId"),
            dateFormat: genaralSettingDateFormat,
        };

        try {
            show();
            const response = await exportEventsVisitorsCSV(payload);

            const blob = new Blob([response.data], { type: 'application/octet-stream' });
            const url = window.URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = url;
            a.download = `${t('export.event-participants')}_${dataInfo?.name}_${getCurrentTimestamp()}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
        } catch (error) {
            toast.error(error?.response?.data?.message || t('common.something-went-wrong'));
        } finally {
            hide();
        }
    };

    return (
        <>
            <div>
                <div className="flex items-center gap-3">
                    <Tooltip title={t("button.select-today")} arrow>
                        <div
                            className="flex items-center justify-center w-10 h-10 cursor-pointer select-none border border-[#E5E7EB] rounded-[6px]"
                            onClick={handleGoToToday}
                        >
                            <CalendarToday
                                fontSize="small"
                                className="text-[#1F2937]"
                            />
                        </div>
                    </Tooltip>


                    <div className="min-w-[280px]">
                        <CustomDateTimePicker
                            mode="single"
                            disableTime
                            showOnlyDate
                            value={selectedDate}
                            onChange={(date) => {
                                setSelectedDate(date);
                                setIsDateFiltered(true);
                            }}
                        />
                    </div>

                    <div className="flex-1">
                        <CustomSearch
                            onSearchChange={handleQuickFilterChange}
                        />
                    </div>

                    <div className="flex rounded-md border border-[#DBE8FF] overflow-hidden bg-[#0057FF] h-10">
                        <button
                            className="flex items-center gap-2 px-4 h-full text-[#FCFCFC] hover:bg-[#00308C] transition"
                            onClick={() => setAddParticipantsOpen(true)}
                        >
                            <AddIcon />
                            {t("button.add")}
                        </button>

                        <button
                            ref={anchorRefAdvanced}
                            id="advanced-button"
                            aria-haspopup="true"
                            onClick={handleToggleAdvanced}
                            className="px-3 h-full flex items-center justify-center text-[#FCFCFC] hover:bg-[#00308C] transition"
                        >
                            <ArrowDropDownIcon />
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-4 2xl:gap-5 mt-4">
                    <LoadingProvider>
                        <VisitorTabGridInfoTable
                            isDateFiltered={isDateFiltered}
                            selectedDate={dayjs(selectedDate).format("YYYY-MM-DD")}
                            searchText={searchText}
                            dataInfo={dataInfo}
                            onRowClick={setSelectedRow}
                            refreshData={refreshData}
                            onRefresh={onRefresh}
                            refresh={refresh}
                            onPaginationChange={handlePaginationChange}
                        />
                    </LoadingProvider>
                </div>
            </div>

            {addParticipantsOpen && (
                <AddParticipantDialog
                    dataInfo={dataInfo}
                    onClose={() => setAddParticipantsOpen(false)}
                    refreshData={refreshData}
                    onRefresh={onRefresh}
                    refresh={refresh}
                />
            )}

            <Popper
                open={openAdvanced}
                anchorEl={anchorRefAdvanced.current}
                placement="bottom-start"
                transition
                disablePortal
                className="z-10"
            >
                {({ TransitionProps, placement }) => (
                    <Grow
                        {...TransitionProps}
                        style={{
                            transformOrigin:
                                placement === "bottom" ? "left top" : "left bottom",
                        }}
                    >
                        <Paper style={{ borderRadius: "8px", }}>
                            <ClickAwayListener onClickAway={handleCloseAdvanced}>
                                <CustomAdvancedMenuList
                                    id="advanced-menu"
                                    aria-labelledby="advanced-button"
                                >
                                    <MenuItem
                                        style={{
                                            borderBottom: "1px solid #E5E7EB",
                                            opacity: "0.5",
                                            pointerEvents: "none",
                                        }}
                                    >
                                        <Link>
                                            <ImportIcon16pxSvg />
                                            <p>{t("button.import-data")}</p>
                                        </Link>
                                    </MenuItem>

                                    <MenuItem
                                        onClick={exportParticipants}
                                        style={{
                                            borderBottom: "1px solid #E5E7EB",
                                        }}
                                    >
                                        <Link>
                                            <ExportIcon16pxSvg />
                                            <p>{t("button.export-data")}</p>
                                        </Link>
                                    </MenuItem>
                                </CustomAdvancedMenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </>
    )
}

export default VisitorsTab